import React, { useState, useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';

import {
  Stack,
  Menu,
  MenuItem,
  Button,
  TableBody,
  TableCell,
  IconButton,
  Dialog,
  TableRow,
  Table,
  TableHead,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Edit,
  CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  DeleteOutline,
  Facebook,
  Instagram,
  WhatsApp,
} from '@mui/icons-material';

import MainContainer from '../MainContainer';
import MainHeader from '../MainHeader';
import MainHeaderButtonsWrapper from '../MainHeaderButtonsWrapper';
import TableRowSkeleton from '../TableRowSkeleton';
import WhatsAppModalAdmin from '../WhatsAppModalAdmin';
import ConfirmationModal from '../ConfirmationModal';
import QrcodeModal from '../QrcodeModal';

import { AuthContext } from '../../context/Auth/AuthContext';
import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import { WhatsAppsContext } from '../../context/WhatsApp/WhatsAppsContext';
import toastError from '../../toasts/toastError';
import { getStyled } from './styles';

const CustomToolTip = ({ title, content, children }) => {
  const theme = useTheme();
  const classes = getStyled(theme);

  return (
    <Tooltip
      arrow
      classes={{
        tooltip: classes.tooltip,
        popper: classes.tooltipPopper,
      }}
      title={
        <React.Fragment>
          <Typography gutterBottom color='inherit'>
            {title}
          </Typography>
          {content && <Typography>{content}</Typography>}
        </React.Fragment>
      }
    >
      {children}
    </Tooltip>
  );
};

const WhatsAppModalCompany = ({
  open,
  onClose,
  filteredWhatsapps,
  companyInfos,
}) => {
  const theme = useTheme();
  const classes = getStyled(theme);
  const { user } = useContext(AuthContext);
  const { loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const confirmationModalInitialState = {
    action: '',
    title: '',
    message: '',
    whatsAppId: '',
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState,
  );

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
    setMenuAnchorEl(null);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, []);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, []);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === 'disconnect') {
      setConfirmModalInfo({
        action: action,
        title: i18n.t('connections.confirmationModal.disconnectTitle'),
        message: i18n.t('connections.confirmationModal.disconnectMessage'),
        whatsAppId: whatsAppId,
      });
    }

    if (action === 'delete') {
      setConfirmModalInfo({
        action: action,
        title: i18n.t('connections.confirmationModal.deleteTitle'),
        message: i18n.t('connections.confirmationModal.deleteMessage'),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === 'disconnect') {
      try {
        await api.delete(
          `/whatsappsession/admin/${confirmModalInfo.whatsAppId}`,
        );
        toastSuccess(i18n.t('connections.toasts.disconnected'));
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === 'delete') {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toastSuccess(i18n.t('connections.toasts.deleted'));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
    setConfirmModalOpen(false);
  };

  const handleFacebookLogin = (channelType) => {
    const clientId = import.meta.env.VITE_FACEBOOK_APP_ID;
    const redirectUri = `${window.location.origin}/auth/facebook/callback`;
    let scope =
      'public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management';

    if (channelType === 'instagram') {
      scope =
        'public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management';
    }

    window.location.href = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}`;
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const renderStatusToolTips = (whatsApp) => {
    return (
      <div style={classes.customTableCell}>
        {whatsApp.status === 'DISCONNECTED' && (
          <CustomToolTip
            title={i18n.t('connections.toolTips.disconnected.title')}
            content={i18n.t('connections.toolTips.disconnected.content')}
          >
            <SignalCellularConnectedNoInternet0Bar
              sx={{ color: theme.custom.colors.cancelButton }}
            />
          </CustomToolTip>
        )}
        {whatsApp.status === 'OPENING' && (
          <CircularProgress size={24} sx={classes.buttonProgress} />
        )}
        {whatsApp.status === 'qrcode' && (
          <CustomToolTip
            title={i18n.t('connections.toolTips.qrcode.title')}
            content={i18n.t('connections.toolTips.qrcode.content')}
          >
            <CropFree />
          </CustomToolTip>
        )}
        {whatsApp.status === 'CONNECTED' && (
          <CustomToolTip title={i18n.t('connections.toolTips.connected.title')}>
            <SignalCellular4Bar
              sx={{ color: theme.custom.colors.confirmButton }}
            />
          </CustomToolTip>
        )}
        {(whatsApp.status === 'TIMEOUT' || whatsApp.status === 'PAIRING') && (
          <CustomToolTip
            title={i18n.t('connections.toolTips.timeout.title')}
            content={i18n.t('connections.toolTips.timeout.content')}
          >
            <SignalCellularConnectedNoInternet2Bar color='secondary' />
          </CustomToolTip>
        )}
      </div>
    );
  };

  const renderActionButtons = (whatsApp) => {
    return (
      <>
        {whatsApp.status === 'qrcode' && (
          <Button
            size='small'
            variant='contained'
            color='primary'
            onClick={() => handleOpenQrModal(whatsApp)}
          >
            {i18n.t('connections.buttons.qrcode')}
          </Button>
        )}
        {whatsApp.status === 'DISCONNECTED' && (
          <>
            <Button
              size='small'
              variant='outlined'
              color='primary'
              onClick={() => handleStartWhatsAppSession(whatsApp.id)}
            >
              {i18n.t('connections.buttons.tryAgain')}
            </Button>{' '}
            <Button
              size='small'
              variant='outlined'
              color='secondary'
              onClick={() => handleRequestNewQrCode(whatsApp.id)}
            >
              {i18n.t('connections.buttons.newQr')}
            </Button>
          </>
        )}
        {(whatsApp.status === 'CONNECTED' ||
          whatsApp.status === 'PAIRING' ||
          whatsApp.status === 'TIMEOUT') && (
          <Button
            size='small'
            variant='outlined'
            onClick={() => {
              handleOpenConfirmationModal('disconnect', whatsApp.id);
            }}
            sx={{
              ...theme.custom.cancelButton,
            }}
          >
            {i18n.t('connections.buttons.disconnect')}
          </Button>
        )}
        {whatsApp.status === 'OPENING' && (
          <Button size='small' variant='outlined' disabled color='default'>
            {i18n.t('connections.buttons.connecting')}
          </Button>
        )}
      </>
    );
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case 'facebook':
        return <Facebook sx={{ color: '#3b5998' }} />;
      case 'instagram':
        return <Instagram sx={{ color: '#e1306c' }} />;
      case 'whatsapp':
        return <WhatsApp sx={{ color: '#25D366' }} />;
      default:
        return 'error';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      scroll='paper'
    >
      <MainContainer>
        <ConfirmationModal
          title={confirmModalInfo.title}
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={handleSubmitConfirmationModal}
        >
          {confirmModalInfo.message}
        </ConfirmationModal>
        <QrcodeModal
          open={qrModalOpen}
          onClose={handleCloseQrModal}
          whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
        />
        <WhatsAppModalAdmin
          open={whatsAppModalOpen}
          onClose={handleCloseWhatsAppModal}
          whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
        />

        <Paper sx={classes.mainPaper} variant='outlined'>
          <MainHeader>
            <Stack>
              <Typography
                variant='h5'
                sx={{
                  fontWeight: 'bold',
                  marginLeft: '10px',
                  marginTop: '10px',
                  color: theme.custom.colors.text,
                }}
                gutterBottom
              >
                Conexões de: {companyInfos?.name}
              </Typography>
            </Stack>

            <MainHeaderButtonsWrapper>
              <Button
                variant='outlined'
                sx={theme.custom.editButton}
                onClick={handleMenuOpen}
              >
                Adicionar Canal
              </Button>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={() => {
                    handleOpenWhatsAppModal();
                    handleMenuClose();
                  }}
                >
                  <WhatsApp
                    fontSize='small'
                    sx={{
                      marginRight: '10px',
                      color: '#25D366',
                    }}
                  />
                  WhatsApp
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleFacebookLogin('facebook');
                    handleMenuClose();
                  }}
                >
                  <Facebook
                    fontSize='small'
                    sx={{
                      marginRight: '10px',
                      color: '#3b5998',
                    }}
                  />
                  Facebook
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleFacebookLogin('instagram');
                    handleMenuClose();
                  }}
                >
                  <Instagram
                    fontSize='small'
                    sx={{
                      marginRight: '10px',
                      color: '#e1306c',
                    }}
                  />
                  Instagram
                </MenuItem>
              </Menu>
            </MainHeaderButtonsWrapper>
          </MainHeader>
          <Stack
            sx={{
              overflowY: 'auto',
              padding: '20px',
              backgroundColor: 'rgb(244 244 244 / 53%)',
              borderRadius: '5px',
            }}
          >
            <Paper>
              <Table size='small'>
                <TableHead sx={classes.TableHead}>
                  <TableRow sx={{ color: '#fff' }}>
                    <TableCell sx={{ color: '#fff' }} align='center'>
                      Channel
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }} align='center'>
                      {i18n.t('connections.table.name')}
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }} align='center'>
                      {i18n.t('connections.table.status')}
                    </TableCell>
                    {user.profile === 'admin' && (
                      <TableCell sx={{ color: '#fff' }} align='center'>
                        {i18n.t('connections.table.session')}
                      </TableCell>
                    )}
                    <TableCell sx={{ color: '#fff' }} align='center'>
                      {i18n.t('connections.table.lastUpdate')}
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }} align='center'>
                      {i18n.t('connections.table.default')}
                    </TableCell>
                    {user.profile === 'admin' && (
                      <TableCell sx={{ color: '#fff' }} align='center'>
                        {i18n.t('connections.table.actions')}
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRowSkeleton />
                  ) : (
                    <>
                      {filteredWhatsapps?.length > 0 &&
                        filteredWhatsapps.map((whatsApp) => (
                          <TableRow
                            key={whatsApp.id}
                            sx={{
                              backgroundColor:
                                theme.custom.colors.defaultBackground,
                            }}
                          >
                            <TableCell align='center'>
                              {IconChannel(whatsApp.channel)}
                            </TableCell>
                            <TableCell align='center'>
                              {whatsApp?.name}
                            </TableCell>
                            <TableCell align='center'>
                              {renderStatusToolTips(whatsApp)}
                            </TableCell>
                            {user.profile === 'admin' && (
                              <TableCell align='center'>
                                {renderActionButtons(whatsApp)}
                              </TableCell>
                            )}
                            <TableCell align='center'>
                              {format(
                                parseISO(whatsApp.updatedAt),
                                'dd/MM/yy HH:mm',
                              )}
                            </TableCell>
                            <TableCell align='center'>
                              {whatsApp.isDefault && (
                                <div style={classes.customTableCell}>
                                  <CheckCircle
                                    sx={{
                                      color: theme.custom.colors.confirmButton,
                                    }}
                                  />
                                </div>
                              )}
                            </TableCell>
                            {user.profile === 'admin' && (
                              <TableCell align='center'>
                                <IconButton
                                  size='small'
                                  onClick={() => handleEditWhatsApp(whatsApp)}
                                >
                                  <Edit />
                                </IconButton>

                                <IconButton
                                  size='small'
                                  onClick={() => {
                                    handleOpenConfirmationModal(
                                      'delete',
                                      whatsApp.id,
                                    );
                                  }}
                                >
                                  <DeleteOutline
                                    sx={{
                                      color: theme.custom.colors.cancelButton,
                                    }}
                                  />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                    </>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </Stack>
        </Paper>
      </MainContainer>
    </Dialog>
  );
};

export default React.memo(WhatsAppModalCompany);
